import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Pencil, Plus, RotateCcw, Save, Target, Trash2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

export function CommercialsTerritoriesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', telephone: '', color: '#71717A', role: '', departments: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/commercials');
      setItems(data || []);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      prenom: c.prenom || '', nom: c.nom || '', email: c.email || '', telephone: c.telephone || '',
      color: c.color || '#71717A', role: c.role || '',
      departments: (c.departments || []).join(', '),
    });
  };
  const openCreate = () => {
    setEditing({ id: null });
    setForm({ prenom: '', nom: '', email: '', telephone: '', color: '#71717A', role: '', departments: '' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        prenom: form.prenom, nom: form.nom, email: form.email, telephone: form.telephone,
        color: form.color, role: form.role,
        departments: form.departments.split(',').map(d => d.trim().toUpperCase()).filter(Boolean),
      };
      if (editing.id) {
        await api.put(`/commercials/${editing.id}`, payload);
        toast.success('Commercial mis à jour');
      } else {
        await api.post('/commercials', payload);
        toast.success('Commercial ajouté');
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setBusy(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.prenom} ${c.nom} ? Les prospects affectés seront détachés.`)) return;
    try {
      await api.delete(`/commercials/${c.id}`);
      toast.success('Commercial supprimé');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const runAutoAssign = async (overwrite) => {
    if (overwrite && !window.confirm('Réaffecter TOUS les prospects (écrase les affectations manuelles existantes) ?')) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/commercials/auto-assign-prospects?overwrite=${overwrite ? 'true' : 'false'}`);
      toast.success(`Auto-affectation : ${data.updated} mis à jour, ${data.skipped} ignorés, ${data.no_match} sans département`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setBusy(false);
  };

  return (
    <Card className="border-2 border-[#8B5CF6]/20 shadow-sm bg-gradient-to-br from-[#F5F3FF] to-white" data-testid="commercials-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-5 h-5 text-[#8B5CF6]" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Carte des commerciaux</h3>
              <p className="text-xs text-[#71717A] mt-0.5">Affecte automatiquement chaque prospect au bon commercial selon son département.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => runAutoAssign(false)} disabled={busy}
              className="h-8 text-xs" data-testid="auto-assign-soft">
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <Target className="w-3 h-3 mr-1"/>}
              Affecter (nouveaux)
            </Button>
            <Button size="sm" variant="outline" onClick={() => runAutoAssign(true)} disabled={busy}
              className="h-8 text-xs border-[#F97316]/30 text-[#EA580C] hover:bg-[#FFF7ED]" data-testid="auto-assign-overwrite">
              <RotateCcw className="w-3 h-3 mr-1"/>Réaffecter tout
            </Button>
            <Button size="sm" onClick={openCreate}
              className="h-8 text-xs bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="add-commercial-btn">
              <Plus className="w-3 h-3 mr-1"/>Ajouter
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-[#71717A]">Chargement...</p>
        ) : (
          <div className="space-y-2">
            {items.map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-[#E5E7EB]"
                data-testid={`commercial-row-${c.id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ background: c.color || '#71717A' }}>
                  {c.prenom?.[0]}{c.nom?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{c.prenom} {c.nom}
                    {c.role && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#F3F4F6] text-[#71717A] rounded">{c.role}</span>}
                  </p>
                  <p className="text-xs text-[#71717A] truncate">
                    {c.email}{c.telephone ? ` · ${c.telephone}` : ''} · <strong>{(c.departments||[]).length}</strong> dpts
                  </p>
                  <p className="text-[10px] text-[#A1A1AA] mt-0.5 font-mono">
                    {(c.departments || []).slice(0, 20).join(' · ')}
                    {(c.departments || []).length > 20 && ` … +${c.departments.length - 20}`}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(c)}
                  className="h-8 w-8 p-0 text-[#8B5CF6] hover:bg-[#F5F3FF]"
                  title="Modifier ce commercial (départements, contact, couleur…)"
                  data-testid={`edit-commercial-${c.id}`}>
                  <Pencil className="w-3.5 h-3.5"/>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(c)}
                  className="h-8 w-8 p-0 text-[#EF4444] hover:bg-[#FEF2F2]" data-testid={`del-commercial-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Editor */}
        {editing && (
          <Card className="border border-[#8B5CF6]/30 bg-white">
            <CardContent className="p-4">
              <p className="text-sm font-semibold mb-3">{editing.id ? 'Modifier' : 'Nouveau commercial'}</p>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Prénom</Label><Input value={form.prenom} onChange={e=>setForm(f=>({...f,prenom:e.target.value}))} required className="h-9" /></div>
                <div><Label className="text-xs">Nom</Label><Input value={form.nom} onChange={e=>setForm(f=>({...f,nom:e.target.value}))} required className="h-9" /></div>
                <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} required className="h-9" /></div>
                <div><Label className="text-xs">Téléphone</Label><Input value={form.telephone} onChange={e=>setForm(f=>({...f,telephone:e.target.value}))} className="h-9" /></div>
                <div><Label className="text-xs">Couleur</Label><Input type="color" value={form.color} onChange={e=>setForm(f=>({...f,color:e.target.value}))} className="h-9 p-1" /></div>
                <div><Label className="text-xs">Rôle (optionnel)</Label><Input value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} placeholder="ex: Directeur Commercial" className="h-9" /></div>
                <div className="col-span-2">
                  <Label className="text-xs">Départements (séparés par virgule)</Label>
                  <Textarea rows={3} value={form.departments}
                    onChange={e=>setForm(f=>({...f,departments:e.target.value}))}
                    placeholder="01, 02, 03, 2A, 2B"
                    className="text-xs font-mono" data-testid="commercial-departments-input" />
                  <p className="text-[10px] text-[#71717A] mt-1">Codes département officiels (01-95, 2A, 2B). Laisser vide pour un commercial à attribution manuelle uniquement.</p>
                </div>
                <div className="col-span-2 flex gap-2 pt-1">
                  <Button type="button" variant="outline" onClick={()=>setEditing(null)} className="flex-1 h-9 text-xs">Annuler</Button>
                  <Button type="submit" disabled={busy} className="flex-1 h-9 text-xs bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="submit-commercial-btn">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <Save className="w-3 h-3 mr-1"/>}
                    {editing.id ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}
